import { useQueryClient, type UseQueryOptions } from 'react-query'

import { useMutationApi, useQueryApi } from 'utils/api'
import {
  handleError,
  validateStatus,
  apiPaths,
  queryKeys,
} from 'api/kinerja/pengawas-sekolah/config'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { UseMutationOptions } from 'react-query'
import type { AxiosError } from 'axios'
import type {
  TPraktikKinerjaFormName,
  TPraktikKinerjaFormValuesRequestBody,
  TPenilaianPraktikKinerjaFormValues,
  TSubmissionResponse,
} from 'types/kinerja-ps-pegawai'

export function useAPIGetPraktikKinerjaFormValues({
  skpPsId,
  formName,
  opts,
}: {
  skpPsId: string
  formName: TPraktikKinerjaFormName
  opts?: UseQueryOptions<TPenilaianPraktikKinerjaFormValues>
}) {
  return useQueryApi<TPenilaianPraktikKinerjaFormValues>(
    apiPaths.praktikKinerjaForm(skpPsId, formName),
    {
      queryKey: [
        queryKeys.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
        skpPsId,
        formName,
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIPostPraktikKinerjaFormValues({
  skpPsId,
  formName,
  opts,
}: {
  skpPsId: string
  formName: TPraktikKinerjaFormName
  opts?: UseMutationOptions<
    TSubmissionResponse,
    AxiosError,
    TPraktikKinerjaFormValuesRequestBody
  >
}) {
  const queryClient = useQueryClient()

  return useMutationApi<
    TSubmissionResponse,
    AxiosError,
    TPraktikKinerjaFormValuesRequestBody
  >(apiPaths.praktikKinerjaForm(skpPsId, formName), {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      ...opts,
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
          skpPsId,
          formName,
        ])
      },
    },
  })
}

export function useAPIPostPraktikKinerjaRollbackState({
  skpPsId,
  formName,
  opts,
}: {
  skpPsId: string
  formName: TPraktikKinerjaFormName
  opts?: UseMutationOptions<TSubmissionResponse, AxiosError, {}>
}) {
  const queryClient = useQueryClient()
  const { period } = usePeriodContext()

  return useMutationApi<TSubmissionResponse, AxiosError, {}>(
    `${apiPaths.praktikKinerjaForm(skpPsId, formName)}/perubahan-status`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        ...opts,
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA,
            skpPsId,
            formName,
          ])
          queryClient.invalidateQueries([queryKeys.API_GET_STATUS, period.id])
        },
      },
    }
  )
}

export function useAPIPostPraktikKinerjaRollbackStateApproval({
  skpPsId,
  formName,
  approvalState,
  opts,
}: {
  skpPsId: string
  formName: TPraktikKinerjaFormName
  approvalState: 'APPROVED' | 'REJECTED'
  opts?: UseMutationOptions<TSubmissionResponse, AxiosError, {}>
}) {
  const queryClient = useQueryClient()
  const { period } = usePeriodContext()

  return useMutationApi<TSubmissionResponse, AxiosError, {}>(
    `${apiPaths.praktikKinerjaForm(
      skpPsId,
      formName
    )}/perubahan-status/${approvalState}`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        ...opts,
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_NOTIFICATION_LIST,
            period.id,
          ])
        },
      },
    }
  )
}
